import Layout from '../components/Layout';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface PrivacySection {
  id: string;
  title: string;
  content: React.ReactNode;
}

const Privacy = () => {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    section1: true
  });

  const toggleSection = (sectionId: string) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const sections: PrivacySection[] = [
    {
      id: "section1",
      title: "1. Information We Collect",
      content: (
        <div>
          <p className="mb-4">
            Fembot collects and stores the following information:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Discord Server IDs where the bot is invited</li>
            <li>User IDs for command usage and moderation functions</li>
            <li>Command usage data for analytics and improvement purposes</li>
            <li>Custom configurations set by server administrators</li>
            <li>Message content when explicitly requested by certain commands</li>
            <li>Error logs to improve bot performance and stability</li>
            <li>Premium subscription data for paying customers</li>
          </ul>
          <p className="mb-4">
            We do not collect or store personal messages outside of command contexts or any information not necessary for the bot's functionality.
          </p>
          <p>
            When you use our AI features, message content used with these commands may be processed by third-party AI providers, subject to their own privacy policies. We maintain only temporary logs of AI interactions to ensure proper functioning and prevent abuse.
          </p>
        </div>
      )
    },
    {
      id: "section2",
      title: "2. How We Use Your Information",
      content: (
        <div>
          <p className="mb-4">
            The information we collect is used for the following purposes:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Providing and maintaining bot functionality</li>
            <li>Implementing server-specific configurations</li>
            <li>Processing and executing commands</li>
            <li>Maintaining moderation logs when enabled</li>
            <li>Analyzing usage patterns to improve features</li>
            <li>Troubleshooting and fixing issues</li>
            <li>Managing premium subscriptions and features</li>
            <li>Security monitoring to detect and prevent abuse</li>
            <li>Communicating with users about important updates or changes</li>
          </ul>
          <p>
            We use aggregated, anonymized data to monitor overall usage patterns and improve our service. This includes metrics like command usage frequency, server sizes, feature popularity, and error rates.
          </p>
        </div>
      )
    },
    {
      id: "section3",
      title: "3. Data Retention and Deletion",
      content: (
        <div>
          <p className="mb-4">
            Server data is retained as long as Fembot remains in your server. When the bot is removed from a server, all configuration data for that server is automatically deleted within 30 days. Certain anonymized analytics data may be retained for historical analysis purposes.
          </p>
          <p className="mb-4">
            Users can request deletion of their specific data by contacting us through our support server or via email. We will process such requests within 30 days, subject to any legal obligations that may require us to retain certain information.
          </p>
          <p className="mb-4">
            For premium subscribers, payment and subscription information is retained for the duration of the active subscription and for a period afterward as required for financial record-keeping purposes. You may request deletion of payment information after your subscription has ended.
          </p>
          <p>
           By default, moderation logs are retained for 90 days, this period can be changed through a manual support request.
          </p>
        </div>
      )
    },
    {
      id: "section4",
      title: "4. Data Security",
      content: (
        <div>
          <p className="mb-4">
            We implement appropriate technical and organizational measures to protect your data against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
          </p>
          <p className="mb-4">
            Our security practices include:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Encryption of sensitive data at rest and in transit</li>
            <li>Regular security audits and vulnerability assessments</li>
            <li>Access controls that restrict data access to authorized personnel</li>
            <li>Continuous monitoring for suspicious activities</li>
            <li>Regular backups to prevent data loss</li>
            <li>Prompt security updates to address vulnerabilities</li>
          </ul>
          <p className="mb-4">
            In the event of a data breach that affects your personal information, we will notify you in accordance with applicable laws and regulations.
          </p>
          <p>
            While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security. By using Fembot, you acknowledge these inherent risks.
          </p>
        </div>
      )
    },
    {
      id: "section5",
      title: "5. Third-Party Services",
      content: (
        <div>
          <p className="mb-4">
            Fembot may use third-party services for certain functionalities. When interacting with these features, users may be subject to those services' privacy policies. We encourage users to review the privacy policies of any external services they interact with through our bot.
          </p>
          <p className="mb-4">
            Third-party services we may utilize include:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>AI and machine learning providers for processing AI-related commands</li>
            <li>Payment processors for handling premium subscriptions</li>
            <li>Analytics services to help us understand usage patterns</li>
            <li>Cloud hosting services for bot infrastructure</li>
          </ul>
          <p className="mb-4">
            For each third-party service, we carefully review their security and privacy practices and only share the minimum data necessary for the functionality to work.
          </p>
          <p>
            We regularly review and update our integrations with third-party services to ensure they continue to meet our privacy standards.
          </p>
        </div>
      )
    },
    {
      id: "section6",
      title: "6. Data Sharing",
      content: (
        <div>
          <p className="mb-4">
            We do not sell, trade, or otherwise transfer your information to outside parties. We may share anonymized, aggregated data for analytical purposes or service improvement.
          </p>
          <p className="mb-4">
            We may disclose your information in the following circumstances:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>With your consent or at your direction</li>
            <li>To comply with legal obligations, such as court orders or legal processes</li>
            <li>To protect our rights, property, or safety, or the rights, property, or safety of our users or others</li>
            <li>In connection with a business transfer, such as a merger, acquisition, or asset sale</li>
            <li>With service providers who perform services on our behalf and have agreed to adhere to our privacy standards</li>
          </ul>
          <p className="mb-4">
            Any shared data is subject to strict confidentiality agreements that limit use to the specific purposes outlined and prohibit further sharing.
          </p>
          <p>
            If the ownership of Fembot changes due to a merger, acquisition, or similar event, your information would be transferred to the new owner, who would continue to be bound by this Privacy Policy.
          </p>
        </div>
      )
    },
    {
      id: "section7",
      title: "7. Children's Privacy",
      content: (
        <div>
          <p className="mb-4">
            Fembot is not directed to individuals under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under the age of 13, we will take steps to delete such information.
          </p>
          <p className="mb-4">
            If you are a parent or guardian and believe that your child has provided us with personal information without your consent, please contact us. If we confirm that a user is under the age of 13, we will promptly delete any personal information collected from that user.
          </p>
          <p className="mb-4">
            In accordance with the Children's Online Privacy Protection Act (COPPA) and similar regulations in other jurisdictions, we implement special precautions for any features that might appeal to children.
          </p>
          <p>
            Discord's Terms of Service require users to be at least 13 years old. We expect all users to comply with Discord's age requirements.
          </p>
        </div>
      )
    },
    {
      id: "section8",
      title: "8. International Data Transfers",
      content: (
        <div>
          <p className="mb-4">
            Fembot operates globally, which means your information may be transferred to, stored, and processed in countries other than the one in which you reside. These countries may have data protection laws that are different from those in your country.
          </p>
          <p className="mb-4">
            By using Fembot, you consent to the transfer of your information to countries outside your country of residence, including the United States, which may have different data protection rules than those in your country.
          </p>
          <p className="mb-4">
            We ensure that any international data transfers are subject to appropriate safeguards as required by data protection laws. These safeguards may include:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Standard contractual clauses approved by relevant data protection authorities</li>
            <li>Data processing agreements with our service providers</li>
            <li>Adherence to frameworks like the EU-US Privacy Shield (when applicable)</li>
          </ul>
          <p>
            If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, we will comply with applicable data protection laws when transferring your personal information outside these regions.
          </p>
        </div>
      )
    },
    {
      id: "section9",
      title: "9. Changes to This Privacy Policy",
      content: (
        <div>
          <p className="mb-4">
            We may update this privacy policy from time to time. We will notify users of any changes by posting the new privacy policy on this page and announcing significant changes in our support server.
          </p>
          <p className="mb-4">
            When we make material changes to this policy, we will notify you through one or more of the following methods:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>A notice on our website</li>
            <li>An announcement in our support server</li>
            <li>A message sent to server owners where Fembot is installed</li>
          </ul>
          <p className="mb-4">
            We encourage you to periodically review this page to stay informed about our information practices and your privacy rights. Your continued use of Fembot after any changes to this Privacy Policy will constitute your acceptance of such changes.
          </p>
          <p>
            The date at the top of this Privacy Policy indicates when it was last updated.
          </p>
        </div>
      )
    },
    {
      id: "section10",
      title: "10. Your Rights",
      content: (
        <div>
          <p className="mb-4">
            Depending on your jurisdiction, you may have the right to:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li><strong>Access</strong> the personal information we have about you</li>
            <li><strong>Correct</strong> inaccurate information</li>
            <li><strong>Request deletion</strong> of your information</li>
            <li><strong>Object to or restrict</strong> processing of your information</li>
            <li><strong>Data portability</strong> (receiving your data in a structured, commonly used format)</li>
            <li><strong>Withdraw consent</strong> for processing based on consent</li>
            <li><strong>Lodge a complaint</strong> with a supervisory authority</li>
          </ul>
          <p className="mb-4">
            To exercise these rights, please contact us through our support server or via email. We will respond to your request within 30 days. We may need to verify your identity before fulfilling your request.
          </p>
          <p className="mb-4">
            For users in the European Union, we serve as a data controller for the information we collect. For certain features where we process data on behalf of server owners (like moderation logs), the server owner may be the data controller, and we act as a data processor.
          </p>
          <p>
            Please note that some data deletion requests may be limited by our legal obligations or legitimate business interests.
          </p>
        </div>
      )
    },
    {
      id: "section11",
      title: "11. Cookies and Tracking",
      content: (
        <div>
          <p className="mb-4">
            Our website may use cookies and similar tracking technologies to enhance your experience, analyze usage patterns, and deliver personalized content. The bot itself does not use cookies directly, but our web dashboard and documentation site do.
          </p>
          <p className="mb-4">
            Types of cookies we use may include:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li><strong>Essential cookies:</strong> Necessary for the website to function properly</li>
            <li><strong>Preference cookies:</strong> Remember your settings and preferences</li>
            <li><strong>Analytics cookies:</strong> Help us understand how visitors interact with our website</li>
            <li><strong>Marketing cookies:</strong> Used to track visitors across websites to enable targeted advertising</li>
          </ul>
          <p className="mb-4">
            You can control cookies through your browser settings. Most browsers allow you to refuse or accept cookies and to delete them. However, if you disable cookies, some features of our website may not function properly.
          </p>
          <p>
            Our analytics providers may collect information such as your IP address, browser type, operating system, referral source, duration of visit, and pages visited. This information is used to improve our website and services.
          </p>
        </div>
      )
    },
    {
      id: "section12",
      title: "12. Premium Subscription Data",
      content: (
        <div>
          <p className="mb-4">
            For users who purchase premium subscriptions, we collect additional information necessary for payment processing and subscription management. This information includes:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Email address</li>
            <li>Discord User ID</li>
            <li>Payment method information (processed securely through our payment processor)</li>
            <li>Billing address (if required by payment processor)</li>
            <li>Subscription history, including plan selected and billing cycles</li>
            <li>Transaction records</li>
          </ul>
          <p className="mb-4">
            This information is used solely for managing your subscription, providing premium features, sending important service notifications, processing payments, and maintaining financial records as required by law.
          </p>
          <p className="mb-4">
            Payment information is processed through secure third-party payment processors who comply with the Payment Card Industry Data Security Standard (PCI DSS). We do not store your full credit card details on our servers.
          </p>
          <p>
            Subscription data is retained for the duration of your subscription and for a period afterward as required for financial record-keeping purposes, typically 7 years as required by tax regulations in many jurisdictions.
          </p>
        </div>
      )
    },
    {
      id: "section13",
      title: "13. Legal Basis for Processing",
      content: (
        <div>
          <p className="mb-4">
            Under the General Data Protection Regulation (GDPR) and other applicable data protection laws, we process your personal data on the following legal grounds:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li><strong>Contractual Necessity:</strong> Processing necessary to provide the services you've requested (operating the bot in your server).</li>
            <li><strong>Legitimate Interests:</strong> Processing necessary for our legitimate business interests, such as improving our services, preventing abuse, and ensuring security.</li>
            <li><strong>Consent:</strong> Processing based on your specific consent, such as for marketing communications or certain data collection activities.</li>
            <li><strong>Legal Obligation:</strong> Processing necessary to comply with our legal obligations.</li>
          </ul>
          <p className="mb-4">
            Where we rely on legitimate interests, we have performed balancing tests to ensure that your rights and interests are not overridden by our interests.
          </p>
          <p>
            Where we rely on your consent, you have the right to withdraw that consent at any time, without affecting the lawfulness of processing based on consent before its withdrawal.
          </p>
        </div>
      )
    },
    {
      id: "section14",
      title: "14. Data Subject Rights for EU Users",
      content: (
        <div>
          <p className="mb-4">
            For users in the European Economic Area (EEA), you have additional rights under the GDPR:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li><strong>Right to be informed:</strong> You have the right to be informed about how we process your personal data.</li>
            <li><strong>Right of access:</strong> You have the right to obtain confirmation as to whether personal data concerning you is being processed, and if so, access to that data.</li>
            <li><strong>Right to rectification:</strong> You have the right to have inaccurate personal data corrected or incomplete data completed.</li>
            <li><strong>Right to erasure (right to be forgotten):</strong> In certain circumstances, you have the right to have your personal data erased.</li>
            <li><strong>Right to restriction of processing:</strong> In certain circumstances, you have the right to restrict the processing of your personal data.</li>
            <li><strong>Right to data portability:</strong> You have the right to receive your personal data in a structured, commonly used, and machine-readable format.</li>
            <li><strong>Right to object:</strong> You have the right to object to processing based on legitimate interests, direct marketing, or research/statistical purposes.</li>
            <li><strong>Rights related to automated decision making and profiling:</strong> You have the right not to be subject to decisions based solely on automated processing that produce legal or similarly significant effects.</li>
          </ul>
          <p className="mb-4">
            To exercise any of these rights, please contact us at lunaxxdev@gmail.com. We will respond to your request within 30 days.
          </p>
        </div>
      )
    },
    {
      id: "section15",
      title: "15. California Privacy Rights",
      content: (
        <div>
          <p className="mb-4">
            If you are a California resident, the California Consumer Privacy Act (CCPA) provides you with specific rights:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li><strong>Right to know:</strong> You have the right to request information about the personal information we collect, use, disclose, and sell.</li>
            <li><strong>Right to delete:</strong> You have the right to request deletion of personal information we have collected about you.</li>
            <li><strong>Right to opt-out of sales:</strong> You have the right to opt-out of the sale of your personal information. Note that we do not sell personal information as defined by the CCPA.</li>
            <li><strong>Right to non-discrimination:</strong> You have the right not to be discriminated against for exercising your CCPA rights.</li>
          </ul>
          <p className="mb-4">
            To exercise your California privacy rights, please contact us at lunaxxdev@gmail.com. We will verify your request using the information associated with your account, including email address. You may also authorize an agent to submit a request on your behalf.
          </p>
          <p>
            In the past 12 months, we have collected the categories of personal information described in the "Information We Collect" section. We have not sold personal information in the past 12 months.
          </p>
        </div>
      )
    },
    {
      id: "section16",
      title: "16. Contact Information",
      content: (
        <div>
          <p className="mb-4">
            If you have any questions about this Privacy Policy, please contact us through our support server or via email at lunaxxdev@gmail.com.
          </p>
          <p className="mb-4">
            For data subject access requests or to exercise your privacy rights, please email us at lunaxxdev@gmail.com with the subject line "Privacy Request".
          </p>
          <p className="mb-4">
            For general support inquiries or to report abuse, please join our support server, which can be accessed through our website.
          </p>
          <p>
            Data Controller: Fembot Team<br />
            Email: lunaxxdev@gmail.com<br />
            Support Server: <a href="https://discord.gg/YjtsNfm2Uk" target="_blank" rel="noopener noreferrer" className="text-fembot-pink hover:underline">Join our Discord</a>
          </p>
        </div>
      )
    }
  ];

  return (
    <Layout>
      <div className="container-custom py-16 md:py-24">
        <h1 className="text-3xl md:text-4xl font-bold mb-8 dark:text-white">Privacy Policy</h1>
        
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            Last updated: April 15, 2025
          </p>
          
          <div className="space-y-6">
            {sections.map((section) => (
              <Collapsible 
                key={section.id} 
                open={openSections[section.id]} 
                onOpenChange={() => toggleSection(section.id)}
                className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
              >
                <CollapsibleTrigger className="w-full px-6 py-4 flex justify-between items-center bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <h2 className="text-xl font-semibold dark:text-white">{section.title}</h2>
                  {openSections[section.id] ? (
                    <ChevronUp className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  )}
                </CollapsibleTrigger>
                <CollapsibleContent className="px-6 py-4 bg-white dark:bg-gray-900">
                  <div className="text-gray-700 dark:text-gray-300">
                    {section.content}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Privacy;
